import { useRef } from "react";
import {
    motion,
    useScroll,
    useTransform,
    useReducedMotion,
} from "framer-motion";

const SERVICES = [
    {
        n: "01",
        title: "International Trade Solutions",
        body: "We facilitate cross-border trade activities between manufacturers, suppliers, distributors and international buyers. Our trade specialists manage sourcing, negotiations, documentation and logistics coordination to ensure efficient and compliant transactions.",
    },
    {
        n: "02",
        title: "FMCG Distribution",
        body: "We source and distribute a broad range of fast-moving consumer goods, including food products, beverages, household products and consumer essentials. Our distribution strategy focuses on reliability, quality and market accessibility.",
    },
    {
        n: "03",
        title: "Halal Product Sourcing",
        body: "As demand for halal-certified products continues to expand globally, we provide specialized halal sourcing solutions that meet international compliance requirements and consumer expectations.",
    },
    {
        n: "04",
        title: "Import & Export Management",
        body: "Our import-export services cover supplier coordination, customs documentation, regulatory compliance, shipping management and international logistics support — helping businesses move products efficiently across borders.",
    },
    {
        n: "05",
        title: "Private Label & Brand Development",
        body: "We support companies seeking to launch proprietary products through private label, OEM and ODM manufacturing partnerships. From product sourcing to packaging and branding, we help create market-ready solutions.",
    },
    {
        n: "06",
        title: "Supply Chain Solutions",
        body: "Our supply chain services include procurement support, inventory planning, logistics coordination, warehousing partnerships and distribution management, enabling businesses to optimize operational performance.",
    },
];

export const Services = () => {
    const ref = useRef(null);
    const reduce = useReducedMotion();
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end end"],
    });

    // Translate inner track horizontally. Cards count = 6, scroll across full set.
    const x = useTransform(scrollYProgress, [0, 1], ["2%", "-78%"]);

    return (
        <section
            ref={ref}
            id="services"
            data-testid="services-section"
            className="relative"
            style={{ height: reduce ? "auto" : "420vh" }}
        >
            <div
                className={`${
                    reduce ? "" : "sticky top-0"
                } h-screen w-full flex flex-col overflow-hidden`}
            >
                {/* Header */}
                <div className="px-4 sm:px-8 lg:px-12 pt-24 pb-8 sm:pb-10">
                    <div className="flex items-center gap-3 font-mono-text text-[10px] sm:text-xs uppercase tracking-[0.3em] text-zinc-500 mb-6">
                        <span className="h-px w-10 bg-[#D69331]" />
                        <span>02 / Core Services</span>
                        <span className="ml-auto hidden sm:inline">
                            Scroll horizontally →
                        </span>
                    </div>
                    <h2 className="font-display uppercase font-bold leading-[0.9] tracking-tight text-white text-3xl sm:text-5xl lg:text-6xl max-w-4xl">
                        Integrated trade capabilities{" "}
                        <span className="text-[#D69331]">for FMCG growth.</span>
                    </h2>
                </div>

                {/* Track */}
                <div className="flex-1 overflow-hidden">
                    <motion.div
                        style={reduce ? {} : { x }}
                        className={`flex h-full ${
                            reduce ? "flex-col gap-px overflow-x-auto" : "flex-row"
                        } gap-px bg-zinc-900 will-change-transform`}
                    >
                        {SERVICES.map((s) => (
                            <article
                                key={s.n}
                                data-testid={`service-card-${s.n}`}
                                className="bg-[#0a0a0a] hover:bg-[#0f0f0f] transition-colors duration-300 group flex flex-col justify-between p-8 sm:p-12 lg:p-16 shrink-0"
                                style={{
                                    width: reduce ? "100%" : "min(85vw, 640px)",
                                }}
                            >
                                <div className="flex items-start justify-between">
                                    <span className="font-display text-6xl sm:text-7xl lg:text-8xl font-bold text-zinc-800 group-hover:text-[#D69331] transition-colors">
                                        {s.n}
                                    </span>
                                    <span className="font-mono-text text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-2">
                                        Service
                                    </span>
                                </div>

                                <div className="mt-8">
                                    <h3 className="font-display uppercase text-2xl sm:text-3xl lg:text-4xl font-semibold leading-tight text-white">
                                        {s.title}
                                    </h3>
                                    <p className="mt-6 font-mono-text text-sm sm:text-base text-zinc-400 leading-relaxed">
                                        {s.body}
                                    </p>
                                </div>

                                <div className="mt-10 pt-6 border-t border-white/10 flex items-center justify-between font-mono-text text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                                    <span>DUHA / {s.n}</span>
                                    <span className="text-[#D69331]">●</span>
                                </div>
                            </article>
                        ))}

                        {/* Closing card */}
                        <article
                            className="bg-[#D69331] text-black flex flex-col justify-between p-8 sm:p-12 lg:p-16 shrink-0"
                            style={{
                                width: reduce ? "100%" : "min(85vw, 640px)",
                            }}
                        >
                            <span className="font-mono-text text-[10px] uppercase tracking-[0.25em]">
                                End of services
                            </span>
                            <div>
                                <h3 className="font-display uppercase text-3xl sm:text-4xl lg:text-5xl font-bold leading-[0.95]">
                                    Need a custom trade workflow?
                                </h3>
                                <a
                                    href="#contact"
                                    data-testid="services-cta"
                                    className="mt-8 inline-flex items-center gap-3 px-6 py-3 bg-black text-[#D69331] font-mono-text text-xs font-bold uppercase tracking-[0.25em] hover:bg-white hover:text-black transition-colors"
                                >
                                    Talk to our team →
                                </a>
                            </div>
                        </article>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
